import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { IconName } from '@frontend/icons';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { OverlappingEventIndicatorVariantType } from './calendar-view-v3/overlapping-event-indicator';

export type EventType = 'appointment' | 'break' | 'outOfOffice' | '' | 'request';

export type TimeRange = {
  startTimestamp: number;
  endTimestamp: number;
};

type LaneInfo = {
  laneIndex: number;
  laneCount: number;
};

type EventProviderInfo = {
  providerId?: string;
  providerName?: string;
};

// For v3 provider or operatory
type EventEntityInfo = {
  entityId?: string;
  entityName?: string;
};

export type EventData = {
  name: string;
  eventId: string;
  startHour: string;
  endHour: string;
  type: string;
  backgroundColor?: string;
  status?: EventStatus;
  locationId?: string;
  id?: string;
  startDate: string;
  endDate: string;
  eventType?: EventType;
  appointmentType?: string;
  appointmentTypeId?: string;
  patientId?: string;
  createdBySourceId?: string;
  workstationIds?: string[];
  isNonIntegratedSource?: boolean;
  referenceId?: string;
  personStatus?: string;
  displayOptions?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  request?: ScheduleRequestEventData;
};

// TimeRange derived from startHour and endHour
// LaneInfo derived based on algorithm to place events in grid
export type ProcessedEventData = EventData &
  TimeRange &
  LaneInfo &
  EventEntityInfo &
  EventProviderInfo & {
    eventType: EventType;
  };

export type CalendarStripeLineInfo = {
  startTime: string; // hh:mm A
  duration: number; // in minutes
};

export type AvailabilityExceptionInfo = CalendarStripeLineInfo;

export type WorkingHourInfo = CalendarStripeLineInfo & {
  breaks?: CalendarStripeLineInfo[];
};

export type CalendarColumnEntity = {
  entityId: string; // providerId or operatoryId (for v3)
  calendarId?: string; // calendarId of provider (for v3)
  entityName: string;
  firstName?: string;
  lastName?: string;
  src?: string;
  iconName?: IconName; // for v3 operatory
  iconColor?: string; // for v3 operatory
  events: EventData[];
  hasAnyWorkingHoursSet?: boolean; // newly added for v3 (which indicate whether any office hours set for provider's any day)
  workingHours?: WorkingHourInfo[];
  availabilityExceptions?: AvailabilityExceptionInfo[]; // newly added for v3
  isAllDayOff?: boolean; // newly added for v3 to indicate if the provider is marked as all day off
  archived?: boolean; // newly added for v3 to indicate if the provider is archived
  requestEvents?: EventData[]; // newly added for v3 to hold schedule requests for the provider
};

export type OfficeCalendarView = {
  officeName: string;
  officeTimezone: string;
  columnEntities?: CalendarColumnEntity[]; // For v3
  officeBreaks?: EventData[]; // Kept it for v2 only
  officeExceptions?: EventData[]; // newly added for v3
  workingHours?: WorkingHourInfo[]; // newly added for v3
  hasAnyWorkingHoursSet?: boolean; // newly added for v3 (which indicate whether any office hours set for any day)
  availabilityExceptions?: AvailabilityExceptionInfo[]; // newly added for v3
  isAllDayOff?: boolean; // newly added for v3 to indicate if the office is marked as all day off
  locationId: string;
  calendarDateValue: string;
};

export type CalendarViewOffices = OfficeCalendarView[];

export type GetPixelFromTimestampParamsType = {
  startHour: number;
  endHour: number;
  totalMinutes: number;
  pixelsPerMinute: number;
};

export type GetGridColumnsFromTimestampParamsType = {
  startHourTimestamp: string;
  endHourTimeStamp: string;
};

export interface LocationWithCalendarColumnEntities {
  id: string;
  name: string;
  timezone: string;
  columnEntities: ColumnEntity[];
}

export interface ColumnEntity {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  avatarVariant: 'square' | 'circle';
  avatarSrc?: string;
  iconName?: IconName;
  iconColor?: string;
  isArchived?: boolean;
  eventCount?: number;
}

/**
 * Shared structural base for all grid calendar event types.
 *
 * Owns the minimum widget contract (id, startTime, duration, eventType,
 * continuesFromPreviousDay, continuesToNextDay) plus the display-enrichment
 * fields shared by every card variant.
 *
 * Note: CalendarEvent from @frontend/calendar-grid is intentionally NOT intersected
 * here. CalendarEvent is a 12-member union, and intersecting a union at the base
 * causes TypeScript to distribute the intersection across all members — producing
 * `never` for mismatched eventType literals and breaking discriminated-union narrowing.
 * Assignability to the widget's renderEventCard / renderEventTooltip signatures is
 * ensured via a cast at the call site in CalendarV2WithGrid / CalendarV3WithGrid.
 */
type GridCalendarEventBase = {
  // ── Widget contract ──────────────────────────────────────────────────────────
  id: string;
  /** Grid placement time in "HH:mm" format */
  startTime: string;
  /** Duration in minutes */
  duration?: number;
  /** true when the event started on the previous day and is clipped to the visible day's start */
  continuesFromPreviousDay?: boolean;
  /** true when the event ends on the next day and is clipped to the visible day's end */
  continuesToNextDay?: boolean;
  // ── Shared display enrichment (all card types) ──────────────────────────────
  title?: string;
  startDate?: string;
  /** Start time in "h:mm A" format — used for display and drag-drop duration calculation */
  startHour?: string;
  /** End time in "h:mm A" format — used for display and drag-drop duration calculation */
  endHour?: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  eventTypeColor?: string;
  backgroundColor?: string;
  calendarDate?: string;
};

export type GridAppointmentCalendarEvent = GridCalendarEventBase & {
  eventType: 'appointment' | 'cancelledAppointment';
  personStatus?: string;
  eventStatus?: EventStatus;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
  /** End date in "YYYY-MM-DD" format — pairs with startDate for duration and pendo tracking */
  endDate?: string;
  /** Appointment type name — shown in the tooltip and used in pendo tracking */
  appointmentTypeName?: string;
  displayOptions?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  // ── Drag-drop fields ────────────────────────────────────────────────────────
  // Carried from the API response so the update mutation and cache invalidation
  // in use-calendar-grid-v3-event-drag-drop.ts have everything they need without
  // a second lookup.
  patientId?: string;
  referenceId?: string;
  appointmentTypeId?: string;
  calendarId?: string;
};

export type GridScheduleRequestCalendarEvent = GridCalendarEventBase & {
  eventType: 'scheduleRequest';
  personStatus?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
  /** Appointment type name — shown in the tooltip */
  appointmentTypeName?: string;
};

export type GridExceptionCalendarEvent = GridCalendarEventBase & {
  eventType: 'exception' | 'unavailableBlock';
};

export type GridWorkingHoursCalendarEvent = GridCalendarEventBase & {
  eventType: 'workingHoursV3';
};

export type GridNonWorkingHoursCalendarEvent = GridCalendarEventBase & {
  eventType: 'nonWorkingHoursV3';
};

export type GridCalendarEvent =
  | GridAppointmentCalendarEvent
  | GridScheduleRequestCalendarEvent
  | GridExceptionCalendarEvent
  | GridWorkingHoursCalendarEvent
  | GridNonWorkingHoursCalendarEvent;

// Draggable event types — single source of truth
export type GridDraggableEvent = GridAppointmentCalendarEvent;
export const isGridDraggableEvent = (event: GridCalendarEvent): event is GridDraggableEvent =>
  ['appointment', 'cancelledAppointment'].includes(event.eventType);

export const isGridAppointmentCalendarEvent = (event: GridCalendarEvent): event is GridAppointmentCalendarEvent =>
  event.eventType === 'appointment' || event.eventType === 'cancelledAppointment';
